// src/data/carriageAtFiling.js
//
// Record 609 (ruling 294, Bob 2026-10-08: "Record by default"; ruling 135). The furtherance submission to the
// Local Chairperson of record is RECORDED BY DEFAULT WHEN THE CLAIM IS FILED. The member may specify otherwise
// afterwards on the claim (CarriagePanel: give for review only, or return carriage).
//
// ONE place, used by the three filing paths - src\pages\NewClaim.js handleSubmit, src\pages\QuickFile.js
// handleSubmit, and src\pages\ClaimDetail.js "File This Claim" (draft -> submitted) - each calling
// recordCarriageAtFiling() ONCE, after its claim write has succeeded. scripts\test-carriage-ui.mjs proves both.
//
// WHEN IT ACTS, and only then:
//   - the claim names its claimant as an account (claimantUid) and THAT account is the one filing - exactly the
//     test carriageAct applies (functions\index.js exports.carriageAct). An officer filing for a member records
//     nothing: carriage moves on HER act, never on an officer's.
//   - the Local Chairperson seat of record for the claim's scopeKey has a holder with a platform account
//     (representativeFor, below - the same test CarriagePanel uses, moved here so there is one of it).
//
// IT NEVER BLOCKS A FILING. Every failure - no seat holder, the function refusing, the network - comes back as a
// line for the screen to show; nothing is thrown. The claim is filed either way.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase';
import { SUBMITTED, FURTHERANCE } from './carriage';

export const AT_FILING_LINE = 'Submitted to your Local Chairperson for furtherance to the carrier (you can change this on the claim)';

// The representative this claim would go to: the Local Chairperson seat of record for its scopeKey.
// Returns { uid, name } or { uid: '', reason } - never a guess. (Moved from CarriagePanel.js, Record 609.)
export function representativeFor(claim, roster, rosterSource) {
  const sk = String((claim && claim.scopeKey) || '');
  if (!sk || sk.endsWith('|UNROUTED')) {
    return { uid: '', reason: 'This claim has not been routed to a Local Chairperson yet, so there is no representative to submit it to.' };
  }
  if (rosterSource === 'loading') return { uid: '', reason: 'Reading the seats of record...' };
  const hit = (Array.isArray(roster) ? roster : []).find((r) => r && r.scopeKey === sk);
  if (!hit) return { uid: '', reason: 'No Local Chairperson seat of record was found for ' + sk + '.' };
  if (hit.source === 'bundle' || rosterSource === 'bundle') {
    return { uid: '', reason: 'The seats of this committee are not yet recorded on the platform, so the holder of the '
      + sk + ' seat cannot be identified as an account.' };
  }
  if (hit.state !== 'held' || !String(hit.holderUid || '').trim()) {
    return { uid: '', reason: 'The ' + sk + ' seat has no holder of record with a platform account'
      + (hit.state === 'vacant' ? ' (the seat is vacant).' : '.') };
  }
  return { uid: String(hit.holderUid).trim(), name: String(hit.holderName || '').trim() };
}

// PURE. What filing should do about carriage. { submit: true, toUid, name } | { submit: false, quiet, reason }.
// quiet: the filer is not the claimant (an officer filing for a member) - nothing to say, nothing to record.
export function carriageAtFilingPlan({ claim, filerUid, roster, rosterSource }) {
  const uid = String(filerUid || '').trim();
  const claimantUid = String((claim && claim.claimantUid) || '').trim();
  if (!uid || !claimantUid || claimantUid !== uid) return { submit: false, quiet: true, reason: '' };
  const rep = representativeFor(claim, roster, rosterSource);
  if (!rep.uid) return { submit: false, quiet: false, reason: rep.reason };
  if (rep.uid === uid) {
    return { submit: false, quiet: false, reason: 'You hold the Local Chairperson seat for this claim yourself; there is no other representative to submit it to.' };
  }
  return { submit: true, toUid: rep.uid, name: rep.name || '' };
}

// Called ONCE per filing, after the claim write succeeded. Resolves to { ok, recorded, line } and never rejects.
export async function recordCarriageAtFiling({ claimId, claim, filerUid, roster, rosterSource }) {
  let plan;
  try {
    plan = carriageAtFilingPlan({
      claim, roster, rosterSource,
      filerUid: filerUid || (auth.currentUser && auth.currentUser.uid) || '',
    });
  } catch (e) {
    return { ok: false, recorded: false, line: 'Your claim is filed. Submitting it to your Local Chairperson could not be prepared ('
      + String((e && e.message) || e) + '). You can submit it from the claim.' };
  }
  if (!plan.submit) {
    if (plan.quiet) return { ok: true, recorded: false, line: '' };
    return { ok: false, recorded: false, line: 'Your claim is filed. It was not submitted to your Local Chairperson automatically: '
      + plan.reason + ' You can submit it from the claim.' };
  }
  if (!claimId) {
    return { ok: false, recorded: false, line: 'Your claim is filed. It was not submitted to your Local Chairperson automatically: the claim has no id yet. You can submit it from the claim.' };
  }
  try {
    const fn = httpsCallable(getFunctions(), 'carriageAct');
    await fn({ claimId, kind: SUBMITTED, purpose: FURTHERANCE, toUid: plan.toUid, specified: '' });
    return { ok: true, recorded: true, line: AT_FILING_LINE + '.' };
  } catch (e) {
    return { ok: false, recorded: false, line: 'Your claim is filed, but submitting it to your Local Chairperson was not recorded: '
      + String((e && e.message) || e) + ' You can submit it from the claim.' };
  }
}
